import { LAYOUT_REGIONS } from './constants'
import type { SlideLayout } from './types/presentation.types'

/**
 * How each slide layout is drawn (MYD-495). Defined once, here, and used by
 * the editor canvas, the sorter thumbnails, the presenter and (Phase 5) the
 * print route, all through `SlideRenderer`.
 *
 * Every size here is in LOGICAL pixels on a 1280x720 stage. The stage is
 * CSS-scaled to whatever box it is shown in, so a 48px heading is 48px on the
 * projector and about 7px on a sorter thumbnail, and the two look the same.
 *
 * Region names and order must match `LAYOUT_REGIONS` (and so the server's);
 * `layouts.test.tsx` checks that.
 *
 * Class strings are written out in full so Tailwind's scanner finds them.
 */

export const STAGE_WIDTH = 1280
export const STAGE_HEIGHT = 720

export interface LayoutSpec {
	label: string
	description: string
	/**
	 * `header`: the slide's title and subtitle sit above a region grid.
	 * `centered`: the title IS the slide (title and section layouts).
	 */
	frame: 'header' | 'centered'
	/** The region grid. Unused for `centered` layouts except `title`'s background. */
	grid: string
	/** Grid placement per region, in LAYOUT_REGIONS order. */
	regions: Record<string, string>
}

export const LAYOUT_SPECS: Record<SlideLayout, LayoutSpec> = {
	title: {
		label: 'Title',
		description: 'A cover: big title and subtitle, over an optional background image.',
		frame: 'centered',
		grid: 'absolute inset-0',
		// Full-bleed behind the text. The renderer dims it so the title stays legible.
		regions: { background: 'absolute inset-0' },
	},
	section: {
		label: 'Section',
		description: 'A divider between parts of a story. Title only.',
		frame: 'centered',
		grid: '',
		regions: {},
	},
	'title-body': {
		label: 'Title and body',
		description: 'One content area under the title.',
		frame: 'header',
		grid: 'grid grid-cols-1',
		regions: { main: 'col-span-1' },
	},
	'two-col': {
		label: 'Two columns',
		description: 'Two equal content areas side by side.',
		frame: 'header',
		grid: 'grid grid-cols-2 gap-10',
		regions: { left: 'col-span-1', right: 'col-span-1' },
	},
	'hero-image': {
		label: 'Hero image',
		description: 'A large image with a caption column beside it.',
		frame: 'header',
		grid: 'grid grid-cols-3 gap-10',
		regions: { hero: 'col-span-2', caption: 'col-span-1' },
	},
	'map-facts': {
		label: 'Map and facts',
		description: 'A map, with key facts beside it.',
		frame: 'header',
		grid: 'grid grid-cols-5 gap-10',
		regions: { map: 'col-span-3', facts: 'col-span-2' },
	},
	'kpi-row': {
		label: 'Key figures',
		description: 'Four headline figures above a content area.',
		frame: 'header',
		grid: 'grid grid-cols-4 grid-rows-[auto_1fr] gap-8',
		regions: {
			'kpi-1': 'col-span-1 row-start-1',
			'kpi-2': 'col-span-1 row-start-1',
			'kpi-3': 'col-span-1 row-start-1',
			'kpi-4': 'col-span-1 row-start-1',
			body: 'col-span-4 row-start-2',
		},
	},
	compare: {
		label: 'Compare',
		description: 'Three columns, for setting assets side by side.',
		frame: 'header',
		grid: 'grid grid-cols-3 gap-8',
		regions: { left: 'col-span-1', center: 'col-span-1', right: 'col-span-1' },
	},
}

/** Human names for regions, for the inspector and the empty-region prompt. */
export const REGION_LABELS: Record<string, string> = {
	background: 'Background',
	main: 'Body',
	left: 'Left',
	right: 'Right',
	center: 'Centre',
	hero: 'Image',
	caption: 'Caption',
	map: 'Map',
	facts: 'Facts',
	'kpi-1': 'Figure 1',
	'kpi-2': 'Figure 2',
	'kpi-3': 'Figure 3',
	'kpi-4': 'Figure 4',
	body: 'Body',
}

export const regionsOf = (layout: SlideLayout): readonly string[] => LAYOUT_REGIONS[layout]

/**
 * Deck themes. One today; the key is stored on every deck so a second theme
 * is a new entry here, not a migration.
 */
export interface ThemeSpec {
	stage: string
	title: string
	subtitle: string
	accent: string
	muted: string
}

export const THEMES: Record<string, ThemeSpec> = {
	default: {
		stage: 'bg-white text-gray-800',
		title: 'text-gray-900',
		subtitle: 'text-gray-500',
		accent: 'text-primary',
		muted: 'text-gray-500',
	},
}

export const themeOf = (key?: string): ThemeSpec => THEMES[key ?? 'default'] ?? THEMES.default
